import { DateTime, type DateTimeFormatOptions } from 'luxon';
import type { TFunction } from '@wix/yoshi-flow-editor';
import type { DurationRange } from '@wix/ambassador-restaurants-operations-v1-operation/types';
import { AdditionalInfoLabel } from '@wix/ambassador-restaurants-v1-fulfillment-method/types';

import {
  DispatchType,
  type DispatchInfo,
  type TimeSlot,
} from '../types/businessTypes';

export const fullDateOptions: DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
};

export const isToday = (date: DateTime, timezone: string) => {
  const today = DateTime.local({ zone: timezone }).startOf('day');

  return date.hasSame(today, 'day');
};

export const isTomorrow = (date: DateTime, timezone: string) => {
  const tomorrow = DateTime.local({ zone: timezone })
    .startOf('day')
    .plus({ days: 1 });

  return date.hasSame(tomorrow, 'day');
};

const getRangeString = ({
  from,
  until,
  t,
}: {
  from: string;
  until: string;
  t: TFunction;
}) => t('menu_olo.dispatch.time.simpleRange', { from, until });

const getScheduledTimeString = (
  { from, until }: { from: DateTime; until: DateTime },
  t: TFunction,
  useFullDateOptions = false,
): string => {
  if (from.valueOf() !== until.valueOf()) {
    return getRangeString({
      from: useFullDateOptions
        ? from.toLocaleString(fullDateOptions)
        : from.toLocaleString(DateTime.TIME_SIMPLE),
      until: until.toLocaleString(DateTime.TIME_SIMPLE),
      t,
    });
  }

  return useFullDateOptions
    ? from.toLocaleString(fullDateOptions)
    : from.toLocaleString(DateTime.TIME_SIMPLE);
};

const convertTimeToTimeString = ({
  from,
  until,
  t,
  timezone,
}: {
  from: DateTime;
  until: DateTime;
  t: TFunction;
  timezone: string;
}): string => {
  const spansMultipleDays = !from.hasSame(until, 'day');

  if (!spansMultipleDays && isToday(from, timezone)) {
    return t('menu_olo.dispatch.time.today', {
      time: getScheduledTimeString({ from, until }, t),
    });
  }
  if (!spansMultipleDays && isTomorrow(from, timezone)) {
    return t('menu_olo.dispatch.time.tomorrow', {
      time: getScheduledTimeString({ from, until }, t),
    });
  }

  return getScheduledTimeString({ from, until }, t, true);
};

const getAsapString = (
  exactTime: number | undefined,
  timeRange: DurationRange | undefined,
  t: TFunction,
): string => {
  if (typeof exactTime === 'number') {
    return t('menu_olo.dispatch.time.upTo.minutes', { upTo: exactTime });
  }
  if (timeRange) {
    return t('menu_olo.dispatch.time.range.minutes', {
      from: timeRange.minDuration,
      until: timeRange.maxDuration,
    });
  }

  return '';
};

const wrapTimeInfoWithPrefix = (
  timeInfo: string,
  dispatchType: DispatchType,
  t: TFunction,
): string => {
  const prefix = t(`header_olo.time.${dispatchType}`);

  return `${prefix}${timeInfo}`;
};

export const formatDispatchTime = ({
  selectedTimeSlot,
  dispatchType,
  t,
  timezone,
  locale,
}: {
  selectedTimeSlot: TimeSlot | undefined;
  dispatchType: DispatchType;
  t: TFunction;
  timezone: string;
  locale: string;
}): string => {
  if (!selectedTimeSlot) {
    return '';
  }

  const { startsNow, startTime, endTime, fulfillmentDetails } =
    selectedTimeSlot;

  if (startsNow) {
    const exactTime = fulfillmentDetails?.maxTimeOptions ?? undefined;
    const timeRange = fulfillmentDetails?.durationRangeOptions ?? undefined;
    const asapString = getAsapString(exactTime, timeRange, t);

    if (!asapString) {
      return '';
    }

    return wrapTimeInfoWithPrefix(asapString, dispatchType, t);
  }

  if (!startTime || !endTime) {
    return '';
  }

  const from = DateTime.fromISO(startTime).setZone(timezone).setLocale(locale);
  const until = DateTime.fromISO(endTime).setZone(timezone).setLocale(locale);

  if (!from.isValid || !until.isValid) {
    return '';
  }

  return wrapTimeInfoWithPrefix(
    convertTimeToTimeString({ from, until, t, timezone }),
    dispatchType,
    t,
  );
};

export const getDineInLabel = ({
  dispatchInfo,
  t,
}: {
  dispatchInfo: DispatchInfo | undefined;
  t: TFunction;
}): string => {
  switch (dispatchInfo?.dineInAdditionalLabel) {
    case AdditionalInfoLabel.CUSTOM:
      return dispatchInfo?.dineInCustomInfo ?? '';

    case AdditionalInfoLabel.TABLE_NUMBER:
      return t('header_olo.address.dineIn.table');

    case AdditionalInfoLabel.ROOM_NUMBER:
      return t('header_olo.address.dineIn.room');

    default:
      return '';
  }
};

export const formatDineInSeating = ({
  dispatchInfo,
  t,
}: {
  dispatchInfo: DispatchInfo | undefined;
  t: TFunction;
}): string => {
  const prefix = getDineInLabel({ dispatchInfo, t });
  const seating = dispatchInfo?.dineInCustomerResponse ?? '';

  return `${prefix} ${seating}`.trim();
};
